import React, { useState, useEffect } from "react";
import ReactMapboxGl, { Marker } from "react-mapbox-gl";

import SearchAddress from "./SearchAddress";
import FindMyLocation from "./FindMyLocation";

import NewDialog from "components/NewDialog";
import NewDialogTitle from "components/NewDialogTitle";
import NewDialogContent from "components/NewDialogContent";
import NewDialogActions from "components/NewDialogActions";

import { mapirApiToken } from "config";
import { useSelector } from "react-redux";
import { mapboxAccessToken } from "config";
import { useGeolocation } from "hooks/geoHook";
import {
  Box,
  Card,
  Typography,
  CardActionArea,
  Stack,
  styled,
} from "@mui/material";
import { setRTLTextPlugin, getRTLTextPluginStatus } from "mapbox-gl";

// import "./styles.css";
import "mapbox-gl/dist/mapbox-gl.css";

const Map = ReactMapboxGl({
  minZoom: 6,
  maxZoom: 20,
  bearingSnap: 10,
  renderWorldCopies: false,
  attributionControl: false,
  accessToken: mapboxAccessToken,
  // interactive: !disabled,
});

const LocationFieldTemplate = (props) => {
  const {
    schema,
    formData,
    // formData = { lat: 32.6539, lng: 51.666, state: "اصفهان", city: "اصفهان" },
    onChange,
    disabled,
    zoom = [15],
    width = "100%",
    height = "280px",
    readonly,
    rawErrors,
  } = props;
  const state = useGeolocation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState([state.longitude, state.latitude]);
  const { mode } = useSelector((state) => state.setting);

  const style =
    mode === "light" ? "/assets/map/green-gray.json" : "/assets/map/dark.json";

  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);

  const handleCenter = (lng, lat) => {
    setCenter([lng, lat]);
    reverseFunction({ lat, lng });
  };

  const handleMapClick = (map, event) =>
    handleCenter(event.lngLat.lng, event.lngLat.lat);

  const reverseFunction = async ({ lat, lng }) => {
    try {
      var url = `https://map.ir/reverse/no?lat=${lat}&lon=${lng}`;

      const response = await fetch(url, {
        headers: {
          "x-api-key": mapirApiToken,
          "Mapir-SDK": "reactjs",
          "Content-Type": "application/json",
        },
      });

      // بررسی وضعیت پاسخ
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const {
        city,
        county,
        country,
        address_compact: address,
        province,
      } = data;

      onChange({
        country,
        address,
        lat: String(lat),
        lng: String(lng),
        state: province,
        city: city || county,
      });
    } catch (error) {
      console.error("Error occurred while fetching data:", error);
      // می‌توانید اینجا اقدامات مورد نظر خود را در صورت بروز خطا انجام دهید
    }
  };

  useEffect(() => {
    const status = getRTLTextPluginStatus();
    if (status === "unavailable") {
      setRTLTextPlugin("/assets/js/mapbox-gl-rtl-text.js");
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        handleCenter(
          formData?.lng || state.longitude,
          formData?.lat || state.latitude
        );
      }
      return setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [formData]);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor:
            rawErrors && rawErrors.length > 0
              ? "error"
              : "action.disabledBackground",
        }}
      >
        <CardActionArea
          onClick={onOpen}
          disabled={disabled || readonly}
          focusRipple={rawErrors && rawErrors.length > 0}
        >
          <Stack
            alignContent="center"
            alignItems="center"
            direction="row"
            height={56}
            px={1.5}
            columnGap={0.5}
          >
            <Typography
              flexGrow={1}
              // fontSize={12}
              align="right"
              variant="body1"
              color="textSecondary"
            >
              {schema.title}
            </Typography>
            {/* <CalendarMonthRoundedIcon color="disabled" fontSize="small" /> */}
          </Stack>
        </CardActionArea>
      </Card>
      <NewDialog label="تقویم" open={open} onClose={onClose}>
        <NewDialogTitle onClose={onClose} title="title" />
        <NewDialogContent isPopup>
          <Stack p={1} flex={1} rowGap={1} bgcolor="background.paper">
            <Card variant="outlined">
              <Box
                width="100%"
                height="100%"
                overflow="hidden"
                bgcolor="#f9f9f9"
                position="relative"
                borderRadius={1}
              >
                <Map
                  zoom={zoom}
                  style={style}
                  center={center}
                  movingMethod="easeTo"
                  onClick={handleMapClick}
                  containerStyle={{ width, height }}
                >
                  <Marker coordinates={center} anchor="center">
                    <StyledBox
                      width={16}
                      height={16}
                      borderRadius={1}
                      sx={{ opacity: 0.9 }}
                      color="primary"
                      bgcolor="primary.main"
                    />
                  </Marker>
                </Map>
                <Stack
                  p={0.5}
                  top={0}
                  left={0}
                  right={0}
                  zIndex={100}
                  rowGap={0.5}
                  position="absolute"
                >
                  <SearchAddress
                    changeCenter={(data) => handleCenter(data[0], data[1])}
                  />
                </Stack>
                <Stack
                  p={0.5}
                  bottom={0}
                  // left={0}
                  right={0}
                  zIndex={100}
                  columnGap={0.5}
                  position="absolute"
                  alignItems="center"
                  direction="row-reverse"
                >
                  <FindMyLocation
                    onClick={(data) => handleCenter(data[0], data[1])}
                  />
                </Stack>
              </Box>
            </Card>
          </Stack>
        </NewDialogContent>
        <NewDialogActions></NewDialogActions>
      </NewDialog>
    </>
  );
};

const StyledBox = styled(Box)(({ theme, color = "primary" }) => ({
  "&::after": {
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    position: "absolute",
    border: "1px solid",
    animation: "ripple 1.2s infinite ease-in-out",
    borderColor: theme.palette[color].main,
    content: '""',
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

export default LocationFieldTemplate;
